import React from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../lib/auth';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function TermsOfService() {
  const { user } = useAuthStore();
  useSEO(
    'Terms of Service | JepPrep',
    'Read the Terms of Service for JepPrep, the trivia training platform. Understand your rights and responsibilities when using our service.',
    { canonical: 'https://jepprep.com/terms' }
  );

  const Section = ({ title, children }) => (
    <div className="mb-8">
      <h2 className="text-lg font-bold text-white mb-3">{title}</h2>
      <div className="text-gray-400 text-sm leading-relaxed space-y-3">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-10" />
            </div>
            {user && (
              <Link to="/dashboard">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold text-sm" data-testid="terms-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-6 h-6 text-[hsl(var(--brand-gold-500))]" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Terms of Service</h1>
        </div>
        <p className="text-gray-500 text-sm mb-10">Last updated: April 4, 2026</p>

        <Section title="1. Acceptance of Terms">
          <p>By accessing or using JepPrep ("the Service"), you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you may not use the Service.</p>
        </Section>

        <Section title="2. Description of Service">
          <p>JepPrep is an independent, fan-made trivia training platform that allows users to practice trivia skills through various game modes including board play, sprint challenges, daily challenges, and community competitions. The Service is not affiliated with, endorsed by, or sponsored by Jeopardy!, Sony Pictures Television, or any of their subsidiaries.</p>
        </Section>

        <Section title="3. User Accounts">
          <p>To access certain features, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate and complete information when creating your account.</p>
          <p>You must be at least 13 years of age to use this Service. By creating an account, you represent that you meet this age requirement.</p>
        </Section>

        <Section title="4. User Conduct">
          <p>You agree not to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
            <li>Attempt to exploit, hack, or disrupt the Service or its infrastructure</li>
            <li>Create boards or content that is offensive, abusive, or violates the rights of others</li>
            <li>Use automated tools, bots, or scripts to interact with the Service</li>
            <li>Impersonate another user or provide false information</li>
            <li>Attempt to manipulate leaderboards, scores, or game outcomes through exploits</li>
          </ul>
        </Section>

        <Section title="5. User-Generated Content">
          <p>Users may create trivia boards and share them with the community. You retain ownership of the content you create, but grant JepPrep a non-exclusive, royalty-free license to display and distribute your content within the platform.</p>
          <p>We reserve the right to remove any user-generated content that violates these terms or is deemed inappropriate at our sole discretion.</p>
        </Section>

        <Section title="6. AI-Generated Content">
          <p>JepPrep uses artificial intelligence to generate trivia questions and paraphrase clues. While we strive for accuracy, AI-generated content may occasionally contain errors. The Service is intended for entertainment and educational purposes and should not be relied upon as a sole source of factual information.</p>
        </Section>

        <Section title="7. Intellectual Property">
          <p>The JepPrep platform, including its design, code, logos, and original content, is the property of JepPrep and is protected by intellectual property laws. Trivia clue data sourced from public archives is used under fair use for educational purposes.</p>
          <p>"Jeopardy!" is a registered trademark of Sony Pictures Television. JepPrep makes no claim to this trademark.</p>
        </Section>

        <Section title="8. Account Termination">
          <p>We reserve the right to suspend or terminate your account at any time if you violate these Terms of Service or engage in conduct that we deem harmful to the Service or other users. You may also delete your account at any time by contacting support.</p>
        </Section>

        <Section title="9. Disclaimer of Warranties">
          <p>The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the Service will be uninterrupted, error-free, or free of harmful components.</p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p>To the maximum extent permitted by law, JepPrep and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service.</p>
        </Section>

        <Section title="11. Changes to Terms">
          <p>We may update these Terms of Service from time to time. When we do, we will revise the "Last updated" date at the top of this page. Continued use of the Service after changes constitutes acceptance of the updated terms.</p>
        </Section>

        <Section title="12. Contact">
          <p>If you have questions about these Terms of Service, please reach out to us through the platform.</p>
        </Section>
      </main>

      <footer className="py-8 bg-[hsl(var(--background))] border-t border-[hsl(var(--border))]">
        <div className="mx-auto max-w-4xl px-4 flex flex-col items-center gap-3">
          <img src={LOGO_URL} alt="JepPrep" className="h-10" />
          <div className="flex items-center gap-4 text-gray-500 text-sm">
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <span>|</span>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
          <p className="text-gray-500 text-sm text-center max-w-2xl">
            © 2026 JepPrep. This application is an independent, fan-made trivia training tool and is not affiliated with, 
            endorsed by, or sponsored by Jeopardy!, Sony Pictures Television, or any of their subsidiaries or affiliates. 
            "Jeopardy!" is a registered trademark of Sony Pictures Television. All trademarks belong to their respective owners.
          </p>
        </div>
      </footer>
    </div>
  );
}
